<template>
  <div class="org-profile-section">
    <api-errors :errors="errors" />
      <el-form class="create-edit-form" :model="form" :rules="rules" ref="form" @change.native="onFormChange">
        <upload
          :label="$t('organization_profile.edit_organization_modal.upload_logo_field.label')"
          @uploadAvatar="handleAvatarUpload"
          class="square"
          :organizationId="organizationId"
          :logo_url="form.image ? logo_cloudfront_path + form.image : ''"
        />
        <div class="form-row">
          <elsa-input
            name="name"
            :label="$t('organization_profile.edit_organization_modal.name_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.name_field.placeholder')"
            v-model.lazy="form.name"
          >
            {{ $t('organization_profile.edit_organization_modal.name_field.label') }}
          </elsa-input>
        </div>
        <div class="form-row">
          <elsa-select
            name="orgType"
            :label="$t('organization_profile.edit_organization_modal.org_type_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.org_type_field.placeholder')"
            :options="orgTypesForUpdate"
            no-data-text="No organization types available"
            @change="onFormChange"
            :disabled="form.orgType === 'k12'"
            v-model.lazy="form.orgType"
          />
          <elsa-input
            name="code"
            :label="$t('organization_profile.edit_organization_modal.code_field.label')"
            :disabled="true"
            :placeholder="$t('organization_profile.edit_organization_modal.code_field.placeholder')"
            v-model.lazy="form.code"
          >
            {{ $t('organization_profile.edit_organization_modal.name_field.label') }}
          </elsa-input>
        </div>
        <div class="form-row address-row">
          <elsa-input
            name="address1"
            :label="$t('organization_profile.edit_organization_modal.address_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.address_field.placeholder')"
            v-model.lazy="form.address1"
          >
            {{ $t('organization_profile.edit_organization_modal.address_field.label') }}
          </elsa-input>
        </div>
        <div class="form-row">
          <elsa-input
            name="city"
            :label="$t('organization_profile.edit_organization_modal.city_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.city_field.placeholder')"
            v-model.lazy="form.city"
          >
            {{ $t('organization_profile.edit_organization_modal.city_field.label') }}
          </elsa-input>
          <elsa-input
            name="state"
            :label="$t('organization_profile.edit_organization_modal.state_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.state_field.placeholder')"
            v-model.lazy="form.state"
          >
            {{ $t('organization_profile.edit_organization_modal.state_field.label') }}
          </elsa-input>
          <elsa-input
            name="postcode"
            type="number"
            :label="$t('organization_profile.edit_organization_modal.post_code_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.post_code_field.placeholder')"
            v-model.lazy="form.postcode"
          >
            {{ $t('organization_profile.edit_organization_modal.post_code_field.label') }}
          </elsa-input>
          <country-select
            name="country"
            :label="$t('organization_profile.edit_organization_modal.country_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.country_field.placeholder')"
            v-model="form.country"
            filterable
            @change="onFormChange"
          />
        </div>
        <div class="form-row">
          <elsa-input
              :label="$t('organization_profile.edit_organization_modal.phone_field.label')"
              name="phone"
              type="number"
              :placeholder="$t('organization_profile.edit_organization_modal.phone_field.placeholder')"
              v-model.lazy="form.phoneNumber"
            >
          </elsa-input>
          <elsa-select
            v-if="isElsaAdmin"
            name="product_type"
            :label="$t('organization_profile.edit_organization_modal.purchasedproduct_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.purchasedproduct_field.placeholder')"
            :options="purchasedProductTypes"
            @change="onFormChange"
            filterable
            multiple
            v-model.lazy="form.purchasedProducts"
          />
        </div>
        <div class="form-row">
          <elsa-select
            name="timezone"
            :label="$t('organization_profile.edit_organization_modal.timezone_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.timezone_field.placeholder')"
            :options="timezones"
            filterable
            :disabled="!isElsaAdmin"
            :information="$t('organization_profile.edit_organization_modal.timezone_field.tooltip_change')"
            v-model.lazy="form.timezone"
          />
          <elsa-select
            name="native_language"
            :label="$t('organization_profile.edit_organization_modal.nativelanguage_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.nativelanguage_field.placeholder')"
            :options="nativeLanguages"
            @change="onFormChange"
            filterable
            v-model.lazy="form.nativeLanguage"
          />
        </div>
        <div class="form-row" v-if="form.orgType != 'k12'">
          <country-select
            name="country_content_restrict"
            :label="$t('organization_profile.edit_organization_modal.countries_content_restrict_field.label')"
            :placeholder="$t('organization_profile.edit_organization_modal.countries_content_restrict_field.placeholder')"
            v-model="form.countryContentRes"
            filterable
            clearable
            :disabled="!isElsaAdmin"
            @change="onFormChange"
          />
        </div>
      </el-form>
      <div class="form-actions">
        <primary-button
          @click.native="preSubmitForm"
          ref="submit"
          :disabled="true"
          v-loading.fullscreen.lock="loading"
        >
          {{ $t('organization_profile.edit_organization_modal.save') }}
        </primary-button>
      </div>
      <confirm-modal
        :dialogVisible="showConfirmModal"
        @closed="showConfirmModal = false"
        @continute="submitForm"
      >
        <template v-slot:body>
            <h4 id="modal-title">
              {{  $t("common.confirm_title") }}
            </h4>
            <p id="modal-desc">
              {{ $t('organization_profile.edit_organization_modal.timezone_field.confirm_desc') }}
            </p>
        </template>
      </confirm-modal>
  </div>
</template>

<script>
  import Upload from '@/components/common/Upload'
  import PrimaryButton from '@/components/common/buttons/PrimaryButton'
  import ElsaInput from '@/components/common/Input'
  import ElsaSelect from '@/components/common/Select'
  import CountrySelect from '@/components/common/CountrySelect'
  import ApiErrors from '@/components/common/ApiErrors'
  import { mapActions } from 'vuex'
  import { path, pathOr, map, either, isEmpty, isNil, contains, values, compose, toLower, sortBy, prop, reject, converge, mergeAll, objOf, propOr } from 'ramda'
  import { handleFormValidation, handleSubmitButtonState } from '@/helpers/formValidationHelper'
  import { successMessage } from '@/utils/messages'
  import timezones from '@/helpers/timezones'
  import { mapGetters } from 'vuex'
  import { getNativeLanguages } from '@/helpers/languages'
  import { PRODUCT_TYPE_OPTIONS } from '@/constants/generic'
  import { K12, SCHOOL_OR_UNIVERSITY } from '@/constants/organizationType';
  import ConfirmModal from '@/components/modals/bases/ConfirmModal'
import { countries } from '@/helpers/countries'

  export default {
    components: {
      PrimaryButton,
      Upload,
      ElsaInput,
      ElsaSelect,
      CountrySelect,
      ApiErrors,
      ConfirmModal
    },
    props: {
      fromAdmin: {
        type: Boolean,
        default: false
      },
      organization: {
        type: Object,
        default: () => {}
      },
      organizationId: {
      }
    },
    data() {
      return {
        toDisable: false,
        organizationUserData: {},
        errors: [],
        form: {
          name: path(['organization', 'name'])(this),
          code: path(['organization', 'code'])(this),
          orgType: path(['organization', 'type'])(this),
          address1: path(['organization', 'address_1'])(this),
          city: path(['organization', 'city'])(this),
          state: path(['organization', 'state'])(this),
          postcode: path(['organization', 'postal_code'])(this),
          country: path(['organization', 'country'])(this),
          phoneNumber: path(['organization', 'phone'])(this),
          image: path(['organization', 'logo_url'])(this),
          timezone: path(['organization', 'timezone'])(this),
          nativeLanguage: path(['organization', 'native_language'])(this),
          purchasedProducts: path(['organization', 'purchased_products'])(this),
          countryContentRes: path(['organization', 'country_content_restrict'])(this),
        },
        originForm: {},
        value: '',
        rules: {
           name: { required: true, message: 'Please input organization name', trigger: 'blur' },
           orgType: { required: true, message: 'Please pick a organization type', trigger: 'blur' },
           address1: { required: true, message: 'Please input a address', trigger: 'blur' },
           phone: { type: 'number', message: 'Phone should be a number', trigger: 'blur' },
           country: { required: true, message: 'Please pick a country', trigger: 'blur' },
           state: { required: true, message: 'Please input a state', trigger: 'blur' },
           city: { required: true, message: 'Please input a city', trigger: 'blur' },
           postcode: [
             { required: true, message: 'Please pick a postcode', trigger: 'blur' }
           ]
        },
        loading: false,
        logo_bucket_path: process.env.VUE_APP_PATH_S3_BUCKET,
        logo_cloudfront_path: process.env.VUE_APP_CLOUDFRONT_AVATAR_URL,
        showConfirmModal: false,
        nativeLanguages: [],
      }
    },
    mounted() {
      if (this.$store.state.organization.types.length === 0) {
        this.getOrganizationTypes()
      }
      // if (!this.isElsaAdmin && this.form.orgType === K12) {
      //   this.form.orgType = SCHOOL_OR_UNIVERSITY
      // }
      this.originForm = { ...this.form }
      this.getNativeLanguages();
    },
    methods: {
      async submitRequest() {
        const organizationFormObject = {
          name: this.form.name,
          type: this.form.orgType,
          address_1: this.form.address1,
          city: this.form.city,
          state: this.form.state,
          postal_code: this.form.postcode,
          country: this.form.country,
          phone: `${this.form.phoneNumber}`,
          status: pathOr('active', ['organization', 'status_id'])(this),
          logo_url: this.form.image,
          timezone: this.form.timezone,
          native_language: this.form.nativeLanguage,
          purchased_products: this.form.purchasedProducts,
          country_content_restrict: this.form.countryContentRes
        }
        // const organizationObject = reject(either(isNil, isEmpty))(organizationFormObject)
        const organizationObject = organizationFormObject
        try {
          this.loading = true;
          const createOrganization = !this.organization
          const organization = await this.updateOrganization({ id: this.organization.id, organization: organizationObject })
          if(!this.fromAdmin) {
            this.$store.dispatch('organization/getCurrentOrganization')
          }
          this.loading = false;
          this.organization ? successMessage(this.$t('organization_profile.edit_organization_modal.update_success'))(this) : successMessage(this.$t('organization_profile.edit_organization_modal.create_success'))(this)

        } catch (error) {
          this.loading = false;
          this.errors = error
        }
      },
      preSubmitForm() {
        const origin_timezone = pathOr('', ['organization', 'timezone'])(this)
        if (this.form.timezone !== origin_timezone) {
          this.showConfirmModal = true
          return
        }
        this.submitForm()
      },
      submitForm() {
        handleFormValidation(this.$refs.form, this.submitRequest, () => { this.dialogVisible = true })
      },
      onFormChange() {
        let requiredFields = {
          name: this.form.name,
          type: this.form.orgType,
          address_1: this.form.address1,
          city: this.form.city,
          state: this.form.state,
          postal_code: this.form.postcode,
          country: this.form.country,
          phoneNumber: this.form.phoneNumber,
        }
        const emptyFields = map(either(isNil, isEmpty), requiredFields)

        handleSubmitButtonState(this.$refs.submit.$el, !contains(true, values(emptyFields)))
      },
      handleAvatarUpload(logo_url) {
        this.form.image = logo_url
      },
      async getNativeLanguages() {
        const nativeLanguages = await getNativeLanguages()
        this.nativeLanguages = nativeLanguages
      },
      ...mapActions({
        updateOrganization: 'admin/updateOrganization',
        getOrganizationTypes: 'organization/getOrganizationTypes',
      }),
      // cancelForm() {
      //   // this.form = {...this.originForm}
      //   // handleSubmitButtonState(this.$refs.submit.$el, false)
      // }
    },
    computed: {
      admins: propOr([], 'currentOrganizationAdmins'),
      ...mapGetters('user', [
        'isElsaAdmin'
      ]),
      ...mapGetters('organization', [
        'isOrganizationAdmin',
        'isOrganizationAssociateAdmin',
        'currentOrganizationAdmins'
      ]),
      timezones() {
        return map(converge(mergeAll, [objOf('value'), objOf('label')]) ,timezones)
      },
      purchasedProductTypes() {
        return PRODUCT_TYPE_OPTIONS;
      },
      orgTypesForUpdate() {
        // add disable to K12 organization types
        return this.$store.state.organization.types.map((type) => {
          if (type.value === K12) {
            return { ...type, disabled: true }
          }
          return type
        })
      },
    },
    watch: {
      organizationData: {
        immediate: true,
        deep: true,
        handler() {
          if(this.fromAdmin) {
            return this.$store.dispatch('organization/setOrganizationAdmins', { organizationId: this.organization.id })
          }
          return this.$store.dispatch('organization/getCurrentOrganizationAdmins')
        }
      },
      form: {
        deep: true,
        handler() {
          return this.onFormChange()
        }
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../scss/_colors.scss";

  .org-profile-section {
    max-width: 900px;
  }

  .box {
    padding: 32px;
    background: white;
  }

  .delete-account {
    text-align: initial;
    .elsa-flat-button.el-button {
      padding: 0;
      color: #87959D;
    }
  }

  .profile {
    .heading {
      display: flex;
      justify-content: space-between;
      margin-top: 24px;
      align-items: flex-start;
    }
    .details {
      margin-top: 32px;
      text-align: initial;
      &::v-deep .info-row:not(:last-of-type) {
        margin-bottom: 22px;
      }
    }
  }

  .account-owner {
    align-self: baseline;
    .owners {
      display: flex;
      margin-top: 24px;
      flex-direction: column;
      .owner {
        display: flex;
        margin-bottom: 32px;
        img {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          margin-right: 30px;
        }
        i {
          flex-grow: 1;
          text-align: end;
          color: $dark-blue-2;
        }
        .info p {
          font-weight: normal;
          font-size: 18px;
          text-transform: capitalize;
          color: $purple;
          margin: 0;
          text-align: initial;
          display: flex;
          align-items: center;
          &:last-of-type {
            font-size: 14px;
            color: $light-blue-2;
            text-transform: none;
            line-height: 34px;
          }
          span {
            font-weight: normal;
            font-size: 11px;
            color: rgba(109, 122, 140, 0.5);
            padding-left: 3px;
            display: flex;
            align-items: center;
            i {
              font-size: 18px;
              vertical-align: text-bottom;
              margin-right: 5px;
            }
          }
        }
      }
    }
    .invite-owner {
      font-weight: 700;
      font-size: 14px;
      color: $purple;
      text-align: initial;
      cursor: pointer;
      i {
        font-size: 20px;
        vertical-align: bottom;
        margin-right: 5px;
      }
    }
  }
  .org-actions {
    margin-top: 60px;
    display: flex;
    .action {
      display: flex;
      font-weight: 600;
      font-size: 14px;
      margin-right: 30px;
      cursor: pointer;
      color: $dark-blue-2;
      align-items: center;
      i {
        margin-right: 7px;
        &::after {
          margin: 0;
          filter: invert(39%) sepia(4%) saturate(2648%) hue-rotate(157deg) brightness(93%) contrast(81%);
        }
      }
      &:hover {
       font-weight: 800;
      }
    }
  }
  .elsa-input.is-error[name="address"] {
    padding-bottom: 20px;
  }
  ::v-deep .create-edit-form {
    .square {
      margin-bottom: 56px;
    }
  }
  #modal-title {
    font-weight: 700;
    font-size: 22px;
    line-height: 32px;
    text-align: center;
    color: $black;
    margin:0 auto;
  }

#modal-desc {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: $black;
  margin:4px auto;

}
</style>
